import {
  ContentContainer,
  FlexColumn,
  FlexRow,
  MaterialIcon,
  StyledButton,
  useLoading,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { Checkbox } from 'baseui/checkbox';
import { HeadingXSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { updateSubClientConfiguration } from '../../../services/subClientService';
import { editSubClientConfigurationState } from '../../../storage/subClients';
import { SubClient, SubClientConfiguration } from '../../../types/subClient';
import StyledTooltip from '../../ui/StyledTooltip';

type Props = {
  subClient: SubClient;
  onReloadPage: () => void;
};

export function ConfigurationTab({ subClient, onReloadPage }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading();
  const { configuration } = subClient;
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const [editingMode, setEditingMode] = useState(false);
  const [editingConfiguration, setEditingConfiguration] = useRecoilState(editSubClientConfigurationState);
  const resetEditingConfiguration = useResetRecoilState(editSubClientConfigurationState);
  const hasUpdates = !areEqual(configuration ?? {}, editingConfiguration ?? {});

  useEffect(() => {
    return () => {
      onCancel();
    };
  }, []);

  const onInputChange = (key: keyof SubClientConfiguration, value: boolean) => {
    setEditingConfiguration((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const onUpdateConfiguration = async () => {
    if (!editingConfiguration) return;
    startLoading();
    const { id, error } = await updateSubClientConfiguration(subClient._id, editingConfiguration);
    if (!error && id) {
      onReloadPage();
      showPositiveFeedback(FEEDBACK.edited(FEEDBACK_PREFIXES.subClientConfiguration, subClient.name));
      onCancel();
    } else {
      showFailFeedback(error || FEEDBACK.failedEdition(FEEDBACK_PREFIXES.subClientConfiguration, subClient.name));
    }
    stopLoading();
  };

  const onCancel = () => {
    setEditingMode(false);
    resetEditingConfiguration();
  };

  return (
    <ContentContainer>
      <FlexColumn
        classNames={css({
          flex: 1,
          width: '50vw',
          maxWidth: '800px',
          boxSizing: 'border-box',
          overflow: 'hidden',
        })}
      >
        <FlexRow
          classNames={`${css({
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: theme.sizing.scale950,
          })}`}
        >
          <HeadingXSmall margin={0}>Configuración del subcliente</HeadingXSmall>
          {!editingMode && (
            <StyledTooltip
              content={'Editar'}
              showArrow={false}
            >
              <Button
                size="compact"
                shape="square"
                onClick={() => setEditingMode(true)}
                kind="tertiary"
              >
                <MaterialIcon
                  name={'edit'}
                  size="xs-mini"
                />
              </Button>
            </StyledTooltip>
          )}
        </FlexRow>
        <Checkbox
          checked={editingMode ? editingConfiguration?.trips : subClient.configuration?.trips}
          disabled={!editingMode}
          onChange={() => onInputChange('trips', !editingConfiguration?.trips)}
        >
          Generación de trayectos
        </Checkbox>
        <Checkbox
          checked={
            editingMode
              ? editingConfiguration?.pushPositionNotifications
              : subClient.configuration?.pushPositionNotifications
          }
          disabled={!editingMode}
          onChange={() => onInputChange('pushPositionNotifications', !editingConfiguration?.pushPositionNotifications)}
        >
          Envío de notificaciones posición
        </Checkbox>
        <Checkbox
          checked={
            editingMode ? editingConfiguration?.pushAlarmNotifications : subClient.configuration?.pushAlarmNotifications
          }
          disabled={!editingMode}
          onChange={() => onInputChange('pushAlarmNotifications', !editingConfiguration?.pushAlarmNotifications)}
        >
          Envío de notificaciones de alarma
        </Checkbox>
        <Checkbox
          checked={editingMode ? editingConfiguration?.reverseGeocode : subClient.configuration?.reverseGeocode}
          disabled={!editingMode}
          onChange={() => onInputChange('reverseGeocode', !editingConfiguration?.reverseGeocode)}
        >
          Georeferencia
        </Checkbox>
        <Checkbox
          checked={editingMode ? editingConfiguration?.faceRekognition : subClient.configuration?.faceRekognition}
          disabled={!editingMode}
          onChange={() => onInputChange('faceRekognition', !editingConfiguration?.faceRekognition)}
        >
          Reconocimiento facial
        </Checkbox>
        {editingMode && (
          <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end', marginTop: 'auto' })}`}>
            <FlexRow gap={theme.sizing.scale300}>
              <StyledButton
                kind="tertiary"
                onClick={onCancel}
              >
                Cancelar
              </StyledButton>
              <StyledButton
                onClick={onUpdateConfiguration}
                isLoading={loading}
                disabled={!hasUpdates}
              >
                Guardar
              </StyledButton>
            </FlexRow>
          </FlexRow>
        )}
      </FlexColumn>
    </ContentContainer>
  );
}
